// C1: the phase hero card that used to live here was demoted to a single
// task line rendered inline by LeagueDashboard/index.tsx (one line + one CTA,
// 法则一). This file keeps the play-options control that the task line and both
// core-loop branches share. G1c: the play menu became a bottom sheet (via the
// shared Modal, no bootstrap menu component in the view layer), with the options
// split into 近 (near) / 远 (far) groups. G2-1/G2-2: 远组 options carry a one-line
// fast-forward preview, and starting a long advance stashes the digest snapshot.
import { useState, type MouseEvent } from "react";
import type { Option } from "../../../../../common/types.ts";
import router from "../../router/index.ts";
import i18n from "../../util/i18n.ts";
import { getSamePathQueryNavigation } from "../../util/samePathIntentNavigation.ts";
import { toWorker } from "../../util/toWorker.ts";
import { Modal } from "../../components/Modal.tsx";
import { classifyPlayOption, type PlayOptionGroup } from "../../util/playMenuGroups.ts";
import { getAdvancePreview } from "./advancePreview.ts";
import { isLongAdvanceOption } from "./advanceDigest.ts";

const optionLabel = (option: Option) =>
  option.labelKey
    ? i18n.t(option.labelKey, {
        ns: "ui",
        defaultValue: option.label,
        ...(option.labelKeyParams ?? {}),
      })
    : option.label;

export const PlayOptionsMenu = ({
  onLongAdvance,
  phase,
  playMenuOptions,
  triggerDefaultLabel,
  triggerLabelKey,
  triggerVariant = "secondary",
}: {
  // G2-2: called just before a long advance runs, so the host can stash the
  // pre-advance snapshot used to render the return digest.
  onLongAdvance?: () => void;
  phase?: number;
  playMenuOptions: Option[];
  // G2-3: the day-done stop point reuses this control as a prominent 推进 CTA.
  triggerDefaultLabel?: string;
  triggerLabelKey?: string;
  triggerVariant?: "primary" | "secondary";
}) => {
  const [show, setShow] = useState(false);

  if (playMenuOptions.length === 0) {
    return null;
  }

  const playOptionsLabel = i18n.t("leagueDashboard.playOptions", {
    ns: "ui",
    defaultValue: "Play options",
  });
  const triggerLabel = triggerLabelKey
    ? i18n.t(triggerLabelKey, {
        ns: "ui",
        defaultValue: triggerDefaultLabel ?? playOptionsLabel,
      })
    : playOptionsLabel;

  const handlePlayOptionClick = (option: Option, event: MouseEvent) => {
    // G2-2: snapshot the "before" state for the return digest whenever a long
    // advance is about to run (multi-day sim or phase-spanning skip). Single-day
    // steps and navigation-only entries do not snapshot, so they never card.
    if (isLongAdvanceOption(option.id, !!option.url)) {
      onLongAdvance?.();
    }
    const samePathIntentUrl = option.url
      ? getSamePathQueryNavigation({
          currentUrl: window.location.href,
          targetUrl: option.url,
        })
      : undefined;
    if (samePathIntentUrl) {
      // The global router preserves a mounted view when only its query changes.
      // Route through it with replace semantics so the host can react to the new
      // intent without adding a duplicate Dashboard entry to the back stack.
      event.preventDefault();
      void router.navigate(samePathIntentUrl, { replace: true });
    } else if (!option.url) {
      event.preventDefault();
      toWorker("playMenu", option.id as any, undefined);
    }
    // Close the sheet once a choice is made (url options navigate away and
    // unmount it anyway; sim commands need it dismissed to reveal progress).
    setShow(false);
  };

  const near = playMenuOptions.filter((option) => classifyPlayOption(option.id) === "near");
  const far = playMenuOptions.filter((option) => classifyPlayOption(option.id) === "far");

  const groupTitle = (group: PlayOptionGroup) =>
    group === "near"
      ? i18n.t("leagueDashboard.playSheetNear", {
          ns: "ui",
          defaultValue: "Play on",
        })
      : i18n.t("leagueDashboard.playSheetFar", {
          ns: "ui",
          defaultValue: "Skip ahead",
        });

  const renderPreview = (option: Option) => {
    // G2-1: 远组 fast-forward preview — the span it will sim, plus a decision-
    // window crossing warning when one is cheaply known. Degrades to nothing.
    const preview = getAdvancePreview(option, phase, playMenuOptions);
    if (!preview) {
      return null;
    }
    return (
      <span
        className="d-block small text-body-secondary mt-1"
        data-mobile-advance-preview={option.id}
      >
        {i18n.t(preview.spanKey, {
          ns: "ui",
          defaultValue: preview.spanDefault,
        })}
        {preview.crossKey ? (
          <span className="d-block text-warning-emphasis" data-mobile-advance-preview-cross="">
            {i18n.t(preview.crossKey, {
              ns: "ui",
              defaultValue: preview.crossDefault ?? "",
            })}
          </span>
        ) : null}
      </span>
    );
  };

  const renderGroup = (group: PlayOptionGroup, options: Option[]) =>
    options.length === 0 ? null : (
      <div className="mobile-play-sheet-group" data-mobile-play-sheet-group={group}>
        <div className="mobile-play-sheet-group-title">{groupTitle(group)}</div>
        {options.map((option, i) => (
          <a
            className="mobile-play-sheet-option"
            data-mobile-play-sheet-option={option.id}
            href={option.url}
            key={i}
            onClick={(event: MouseEvent<any>) => handlePlayOptionClick(option, event)}
          >
            <span className="d-flex flex-column">
              <span>{optionLabel(option)}</span>
              {group === "far" ? renderPreview(option) : null}
            </span>
          </a>
        ))}
      </div>
    );

  return (
    <>
      <button
        className={
          triggerVariant === "primary" ? "btn btn-primary" : "btn btn-outline-secondary btn-sm"
        }
        data-mobile-play-sheet-trigger=""
        data-mobile-play-sheet-option-ids={playMenuOptions.map((option) => option.id).join(" ")}
        id="dashboard-play-options"
        onClick={() => {
          setShow(true);
        }}
        type="button"
      >
        {triggerLabel}
      </button>
      <Modal
        centered
        onHide={() => {
          setShow(false);
        }}
        show={show}
      >
        <Modal.Header closeButton>{playOptionsLabel}</Modal.Header>
        <Modal.Body>
          <div data-mobile-play-sheet="">
            {renderGroup("near", near)}
            {renderGroup("far", far)}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
